import { Component, EventEmitter, Input, Output, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { AppSettings } from '@services/settings/app-settings.service';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import Swal from 'sweetalert2';
import { renderMarkdown } from '../../../../../core/utils/markdown.util';
import { MailHistoryItem, MailManagementService } from '../../services/mail-management.service';

@Component({
  selector: 'app-mail-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, NgbTooltipModule, MobileFooterComponent],
  templateUrl: './mail-detail.component.html',
  styleUrl: './mail-detail.component.scss'
})
export class MailDetailComponent {
  readonly appSettings = inject(AppSettings);
  private mailService = inject(MailManagementService);
  private sanitizer = inject(DomSanitizer);
  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });

  public item = signal<MailHistoryItem | null>(null);
  public detail = signal<(MailHistoryItem & { bodyHtml?: string; bodyText?: string; bodyMd?: string; canResend?: boolean }) | null>(null);
  public loadingBody = signal<boolean>(false);
  public isResending = signal<boolean>(false);
  public isRetrying = signal<boolean>(false);
  public isResendMode = signal<boolean>(false);
  public activeTab = signal<'markdown' | 'html'>('markdown');
  public resendTo = signal<string>('');
  public resendCc = signal<string>('');

  public canResend = computed<boolean>(() => {
    const d = this.detail();
    if (!d) return false;
    return d.canResend ?? true;
  });

  public renderedMarkdown = computed<SafeHtml>(() => {
    const md = this._resolveMarkdown(this.detail());
    return this.sanitizer.bypassSecurityTrustHtml(renderMarkdown(md));
  });

  @Input() set mailItem(val: MailHistoryItem | null) {
    this.item.set(val);
    this.isResendMode.set(false);
    this.resendTo.set('');
    this.resendCc.set('');
    if (val?.uuid) this._loadBody(val.uuid);
    else this.detail.set(null);
  }

  @Output() onResent = new EventEmitter<void>();
  @Output() backToList = new EventEmitter<void>();

  // Carga el cuerpo del correo seleccionado
  private _loadBody(uuid: string): void {
    this.loadingBody.set(true);
    this.mailService.getByUuid(uuid).subscribe({
      next: (res) => { this.detail.set(res); this.loadingBody.set(false); },
      error: () => { this.loadingBody.set(false); }
    });
  }

  // Resuelve la fuente de Markdown con fallback a texto o HTML limpio
  private _resolveMarkdown(d: any): string {
    if (!d) return '';
    if (d.bodyMd?.trim()) return d.bodyMd;
    if (d.bodyText?.trim()) return d.bodyText;
    return this._stripHtmlToMarkdown(d.bodyHtml ?? '');
  }

  // Convierte etiquetas simples de HTML a Markdown básico legible
  private _stripHtmlToMarkdown(html: string): string {
    if (!html) return '';
    return html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<h[1-3][^>]*>(.*?)<\/h[1-3]>/gi, '### $1\n\n')
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
      .replace(/<[^>]+>/g, '')
      .trim();
  }

  // Activa el modo de reenvio habilitando los inputs
  enterResendMode(): void {
    this.isResendMode.set(true);
    this.resendTo.set('');
    this.resendCc.set('');
  }

  // Cancela el modo de reenvio y vuelve a vista
  cancelResendMode(): void {
    this.isResendMode.set(false);
  }

  // Copia el destinatario y copia originales en los inputs
  fillOriginalRecipient(): void {
    this.resendTo.set(this.item()?.toMail ?? '');
    this.resendCc.set(this.item()?.ccMail ?? '');
  }

  // Valida y ejecuta el reenvio del correo
  executeResend(): void {
    const to = this.resendTo().trim();
    if (!this._isValidEmail(to)) {
      Swal.fire('Atención', 'Ingrese un correo de destino válido.', 'warning');
      return;
    }
    this._dispatchResend(to, this.resendCc().trim());
  }

  // Valida formato basico de correo electronico
  private _isValidEmail(mail: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !!mail && regex.test(mail);
  }

  // Envia la peticion de reenvio al servidor
  private _dispatchResend(toMail: string, ccMail: string): void {
    const uuid = this.item()?.uuid;
    if (!uuid) return;
    this.isResending.set(true);
    this.mailService.resend(uuid, { toMail, ccMail: ccMail || undefined }).subscribe({
      next: () => this._handleResendSuccess(),
      error: (err) => this._handleResendError(err)
    });
  }

  // Procesa respuesta exitosa tras reenviar
  private _handleResendSuccess(): void {
    this.isResending.set(false);
    this.isResendMode.set(false);
    this.Toast.fire({ icon: 'success', title: 'Correo reenviado con éxito' });
    this.onResent.emit();
  }

  // Procesa fallo al reenviar correo
  private _handleResendError(err: any): void {
    this.isResending.set(false);
    const msg = err?.error?.message ?? err?.error?.error ?? 'No se pudo reenviar el correo';
    Swal.fire('Error', msg, 'error');
  }

  // Ejecuta reintento inmediato del correo fallido
  executeRetry(): void {
    const uuid = this.item()?.uuid;
    if (!uuid) return;
    this.isRetrying.set(true);
    this.mailService.retry(uuid).subscribe({
      next: () => this._handleRetrySuccess(),
      error: (err) => this._handleRetryError(err)
    });
  }

  // Procesa respuesta exitosa tras reintentar
  private _handleRetrySuccess(): void {
    this.isRetrying.set(false);
    this.Toast.fire({ icon: 'success', title: 'Correo enviado con éxito' });
    this.onResent.emit();
  }

  // Procesa fallo al reintentar correo
  private _handleRetryError(err: any): void {
    this.isRetrying.set(false);
    const msg = err?.error?.message ?? err?.error?.error ?? 'No se pudo reintentar el correo';
    Swal.fire('Error', msg, 'error');
  }
}
